import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Shield,
  PenTool,
  AlertTriangle,
  Loader2,
  Building,
  UserPlus,
  Unlock as UnlockIcon,
  ShieldAlert,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEIMZO } from '@/hooks/useEIMZO';
import { reportEImzoAvailability } from '@/hooks/eimzoAvailability';
import { usePostRequest, useGetRequest } from '@/shared/hooks/requests';
import { EImzoSetupModal } from '@/pages/Auth/EImzoRegistration';
import { CandidatesManager } from './CandidatesManager';
import { VerificationBlock } from './VerificationBlock';
import { SigningActions } from './SigningActions';
import { field, signingState } from '../signingState';
import { placeholderCheck, placeholderErrorMessages, usePlaceholderStatus } from '../placeholderCheck';
import {
  EIMZO_SIGN_PREPARE,
  EIMZO_SIGN_TIMESTAMP,
  EIMZO_SIGN_COMPLETE,
  EIMZO_CERTIFICATES,
  WORKFLOW,
  EIMZO_CREATE_CHALLENGE,
  EIMZO_VERIFY,
} from '@/shared/utils/urls';
import { currentUserUuid, iHold } from '@/shared/auth/me.js';
import { SCOPE } from '@/shared/auth/scopes.js';
import { apiErrorMessage } from '@/shared/utils/apiError';
import { maskNationalId } from '@/shared/utils/nationalId';
import { certFitsSlot, certNationalId, participantIdLabel, participantIsMe, slotTakesCert } from '../participants';

// Connection gate shown while E-IMZO isn't connected: spinner while a
// connect attempt runs (or before the mount auto-connect kicks in), then a
// persistent "module not found" card with install/retry — never an infinite
// spinner when the desktop client simply isn't there.
const EimzoGate = ({ eimzo, onRetry, t }) => (
  eimzo.connecting || !eimzo.error ? (
    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-3 bg-muted/30 rounded-md border border-border">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>{t('signing:eimzo.connecting')}</span>
    </div>
  ) : (
    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md space-y-2">
      <div className="flex gap-2 text-sm text-amber-900 dark:text-amber-200">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">{t('signing:eimzo.notInstalled')}</p>
          <p className="opacity-80">{t('signing:eimzo.notInstalledHint')}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button asChild size="sm" variant="outline" className="flex-1">
          <a href="https://e-imzo.uz/" target="_blank" rel="noreferrer">{t('signing:eimzo.download')}</a>
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={onRetry}>
          {t('signing:eimzo.tryAgain')}
        </Button>
      </div>
    </div>
  )
);

const SignerStatusIcon = ({ status }) => {
  switch (status) {
    case 'signed':
      return <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />;
    case 'rejected':
      return <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />;
    default:
      return <Clock className="w-3.5 h-3.5 text-yellow-500 shrink-0" />;
  }
};

// Who a participant is comes from the server (`is_me`); national IDs arrive
// masked and are shown masked — see ../participants.js.
const isParticipantSigned = (p) => p.status === 'signed';

// A failed step's message: the backend's code through errors.json; a
// signature the backend could not verify by its own key in this namespace.
function stepFailure(res, t) {
  const detail = res?.error?.data?.detail;
  const parsed = res?.errorInfo;
  if (detail?.code === 'signature_error' && detail?.i18nKey) {
    return new Error(t(`signing:interface.errors.${detail.i18nKey}`, { defaultValue: apiErrorMessage(parsed) }));
  }
  return Object.assign(new Error(apiErrorMessage(parsed, placeholderErrorMessages)), { code: parsed?.code || null });
}

// Refusals that mean the signers list on screen is out of date: the caller
// holds no slot (`not_workflow_participant`), or the certificate fills none
// of theirs (`SIGNER_CANDIDATES_NOT_FOUND`: a key registered to someone else).
const STALE_SLOT = new Set(['not_workflow_participant', 'SIGNER_CANDIDATES_NOT_FOUND']);

/**
 * SigningPanel - Right side panel showing signing info and actions
 * Shows when viewing documents with type 'signed' (E-IMZO digital signature)
 *
 * The signed-in person signs with the session cookie (shared/auth/me.js says
 * who they are); a guest signer (`isGuest`, no account) with the token their
 * E-IMZO sign-in gave them (`guestToken`, held by the page in memory).
 */
export const SigningPanel = ({ fileUuid, approvalState, onSigningComplete, onChanged, document, isGuest = false, guestToken = null }) => {
  const { t, i18n } = useTranslation();
  const eimzo = useEIMZO();
  const fileRef = fileUuid || document?.uuid || null;
  // A guest signer's own credential, on every call of theirs.
  const guestHeaders = guestToken
    ? { Authorization: `Bearer ${guestToken}`, 'accept-language': i18n.language }
    : null;
  const withGuest = guestHeaders ? { headers: guestHeaders } : {};
  // Changing the signers, the deadline, the verification page: `signing:write`.
  const canManage = !isGuest && iHold(SCOPE.SIGNING_WRITE);
  // Signing with E-IMZO (and registering a certificate): `signing:sign`; a
  // guest signer by their slot alone (docs/ACCESS.md).
  const canSign = isGuest || iHold(SCOPE.SIGNING_SIGN);

  // State
  const [submitting, setSubmitting] = useState(false);
  const [successResult, setSuccessResult] = useState(null);
  const [showCandidatesManager, setShowCandidatesManager] = useState(false);

  const getRegisteredCertificates = useGetRequest({ url: EIMZO_CERTIFICATES, ...withGuest });

  const getSignersCollection = useGetRequest();

  const refreshCollection = () => {
    // A guest signer reads nothing until their E-IMZO sign-in gave a token.
    if (!fileRef || (isGuest && !guestToken)) return;
    getSignersCollection.request({ url: WORKFLOW.replace('{file_uuid}', fileRef), ...withGuest });
  };

  useEffect(() => {
    refreshCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileRef, isGuest, guestToken, i18n.language]);

  // Process signer collection response
  // Note: We removed automatic successResult setting based on selected certificate
  // because we want the user to see the signing interface and warnings instead of 
  // immediately jumping to the "Signed" view when just switching certificates.
  useEffect(() => {
    const collection = getSignersCollection.response;
    if (!collection || !collection.participants) return;

    // We only set initial successResult if there's no selected certificate yet (first load)
    // and the user has already signed.
    if (!eimzo.selectedCertificate && !successResult) {
      const participants = collection.participants || [];
      const myCandidate = participants.find((c) => participantIsMe(c));

      if (myCandidate && isParticipantSigned(myCandidate) && myCandidate.signature) {
        const sig = myCandidate.signature;
        const normalizedResult = {
          signedAt: sig.signedAt,
          isTimestamped: sig.isTimestamped,
          tsaTimeStr: sig.tsaTimeStr,
          verificationStatus: sig.verificationStatus,
          signerIssuer: sig.certificate?.subjectCommonName,
          signerSerialNumber: sig.signerSubjectSerialNumber || sig.certificate?.serialNumber,
        };
        setSuccessResult(normalizedResult);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSignersCollection.response]);

  const [setupModalOpen, setSetupModalOpen] = useState(false);

  // Each step's failure is told once, by handleDirectSign.
  const prepareSignRequest = usePostRequest({ url: EIMZO_SIGN_PREPARE, showError: false, ...withGuest });
  const timestampSignRequest = usePostRequest({ url: EIMZO_SIGN_TIMESTAMP, showError: false, ...withGuest });
  const completeSignRequest = usePostRequest({ url: EIMZO_SIGN_COMPLETE, showError: false, ...withGuest });

  // For inline cert registration during sign (a person's: a guest signer's
  // certificate was registered by their E-IMZO sign-in)
  const createChallenge = usePostRequest({ url: EIMZO_CREATE_CHALLENGE, showError: false });
  const verifySignature = usePostRequest({ url: EIMZO_VERIFY, showError: false });

  // Combined certificates (local + registered)
  const combinedCertificates = useMemo(() => {
    const localCerts = eimzo.certificates || [];
    const localSerials = new Set(localCerts.map((c) => c.serialNumber?.toLowerCase()));
    const registeredCerts = getRegisteredCertificates.response || [];

    // Format registered certs that are not on device
    const formattedRegistered = registeredCerts
      .filter((reg) => {
        const serial = (reg.serialNumber || reg.serial_number || '').toLowerCase();
        return serial && !localSerials.has(serial);
      })
      .map((reg) => ({
        id: `reg-${(reg.serialNumber || reg.serial_number).toLowerCase()}`,
        serialNumber: (reg.serialNumber || reg.serial_number).toLowerCase(),
        fullName:
          reg.subjectCommonName ?? reg.subject_common_name ?? reg.fullName ?? t('signing:eimzo.registeredKey'),
        O: reg.organization || '', // Only use organization if explicitly provided
        // The user's own certificate; shown masked like every national ID.
        PINFL: reg.nationalIdValue ?? reg.national_id_value ?? reg.nationalIdMasked ?? reg.national_id_masked,
        TIN: reg.nationalIdValue ?? reg.national_id_value ?? reg.nationalIdMasked ?? reg.national_id_masked,
        isOnDevice: false,
        isRegistered: true,
        validTo: (reg.validTo || reg.valid_to) ? new Date(reg.validTo || reg.valid_to) : null,
      }));

    // Merge them together
    return [
      ...localCerts.map((c) => ({
        ...c,
        isOnDevice: true,
        isRegistered: registeredCerts.some(
          (r) => (r.serialNumber || r.serial_number || '').toLowerCase() === c.serialNumber?.toLowerCase()
        ),
      })),
      ...formattedRegistered,
    ].sort((a, b) => {
      // Show on-device and valid first
      if (a.isOnDevice !== b.isOnDevice) return a.isOnDevice ? -1 : 1;
      if (a.isExpired !== b.isExpired) return a.isExpired ? 1 : -1;
      return 0;
    });
  }, [eimzo.certificates, getRegisteredCertificates.response, t]);

  // Update selected certificate data (like isRegistered) when combinedCertificates changes
  useEffect(() => {
    if (eimzo.selectedCertificate && combinedCertificates.length > 0) {
      const serial = eimzo.selectedCertificate.serialNumber;
      const id = eimzo.selectedCertificate.id;
      const updated = combinedCertificates.find((c) => (c.serialNumber === serial && serial) || c.id === id);

      if (updated && (
        updated.isRegistered !== eimzo.selectedCertificate.isRegistered ||
        updated.isOnDevice !== eimzo.selectedCertificate.isOnDevice
      )) {
        eimzo.setSelectedCertificate(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedCertificates]);

  // Auto-select the only authorized certificate for the current document.
  // If exactly one device cert fits a slot that still owes a signature,
  // pre-select it so the user only needs to click "Sign".
  useEffect(() => {
    // Already chose one — don't override
    if (eimzo.selectedCertificate) return;

    if (!combinedCertificates || combinedCertificates.length === 0) return;
    const collection = getSignersCollection.response;
    if (!collection?.participants || collection.participants.length === 0) return;

    // Slots that still owe a signature (rejected slots don't authorize anything).
    const pendingCandidates = collection.participants.filter(c => c.status === 'pending');

    const matches = combinedCertificates.filter(cert => {
      if (!cert.isOnDevice || cert.isExpired) return false;
      return Boolean(certNationalId(cert)) && pendingCandidates.some(c => slotTakesCert(c, cert));
    });

    if (matches.length === 1) {
      eimzo.setSelectedCertificate(matches[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedCertificates, getSignersCollection.response]);

  // Check if the selected certificate's holder (me) has already signed this
  // document: a signed slot of mine that this certificate fits.
  const isSelectedCertAlreadySigned = useMemo(() => {
    const collection = getSignersCollection.response;
    if (!eimzo.selectedCertificate || !collection?.participants) return false;
    if (!certNationalId(eimzo.selectedCertificate)) return false;

    return collection.participants.some(c =>
      isParticipantSigned(c)
      && participantIsMe(c)
      && certFitsSlot(eimzo.selectedCertificate, c)
    );
  }, [eimzo.selectedCertificate, getSignersCollection.response]);

  // Check if the selected certificate is authorized for this document: a slot
  // of mine it fits (a slot listed by account alone takes any of my
  // certificates; the backend backfills the national ID on sign).
  const isUserCandidate = useMemo(() => {
    const collection = getSignersCollection.response;
    if (!eimzo.selectedCertificate || !collection?.participants) return false;
    if (!certNationalId(eimzo.selectedCertificate)) return false;

    return collection.participants.some(c =>
      slotTakesCert(c, eimzo.selectedCertificate)
    );
  }, [eimzo.selectedCertificate, getSignersCollection.response]);

  // Every candidate has signed — document is fully executed
  const allCandidatesSigned = useMemo(() => {
    const participants = getSignersCollection.response?.participants;
    if (!participants || participants.length === 0) return false;
    return participants.every(isParticipantSigned);
  }, [getSignersCollection.response]);

  // Full signer collection data for UI
  const signerCollection = getSignersCollection.response || null;

  // Declined (halted), withdrawn, or superseded: nothing more to sign or change.
  const { ended: signingEnded, deadlinePassed } = signingState(signerCollection);

  // Required template fields still empty, when the install's signing policy
  // checks them ('warn' tells, 'block' holds the signature back).
  const placeholdersMode = field(field(signerCollection, 'signing_policy'), 'placeholders') || 'off';
  const placeholderStatus = usePlaceholderStatus(
    approvalState === 'ordinary' ? null : fileRef,
    placeholdersMode,
    guestToken,
  );
  const placeholders = placeholderCheck(placeholdersMode, placeholderStatus);
  const placeholdersBlock = placeholders?.mode === 'block';
  const signingActions = (
    <SigningActions
      collection={signerCollection}
      fileUuid={fileRef}
      isGuest={isGuest}
      guestToken={guestToken}
      document={document}
      onChanged={(response) => {
        refreshCollection();
        onChanged?.(response);
      }}
    />
  );


  // Connect to E-IMZO and load certificates
  const handleConnectEIMZO = async () => {
    let clientReachable = false;
    try {
      await eimzo.connect();
      clientReachable = true;
      reportEImzoAvailability(true);
      await eimzo.loadCertificates();
      getRegisteredCertificates.request();
    } catch {
      if (!clientReachable) {
        // Client unreachable: the persistent amber card explains it — a
        // toast on top would just be noise.
        reportEImzoAvailability(false);
      } else {
        toast.error(t('signing:eimzo.connectionFailed'));
      }
    }
  };

  // Auto-connect to E-IMZO when the panel mounts (only for signed docs that
  // need it, and only for someone who may sign).
  useEffect(() => {
    if (canSign && !eimzo.connected && !eimzo.connecting) {
      handleConnectEIMZO();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle certificate selection
  const handleSelectCertificate = (certId) => {
    const cert = combinedCertificates.find((c) => (c.serialNumber || c.id) === certId);
    eimzo.setSelectedCertificate(cert);
  };

  // Handle direct sign (solo flow)
  const handleDirectSign = async () => {
    if (!eimzo.selectedCertificate) {
      toast.error(t('signing:interface.selectCertificate'));
      return;
    }

    try {
      setSubmitting(true);

      // Step 1: Load Key (single password prompt for the whole flow)
      const keyId = await eimzo.loadKey(eimzo.selectedCertificate);

      // Step 2: If this cert is not yet registered on the backend, do it
      // inline using the same loaded key. The user only sees ONE password
      // prompt for both registration and signing. This comes BEFORE prepare:
      // a signer listed by PINFL alone holds their slot through a registered
      // certificate with that PINFL (WorkflowService.is_participant), so
      // prepare answers 404 until docs knows the key.
      // A guest signer proved their certificate at sign-in (it is theirs at
      // docs already) and may call only the signing routes: never this step.
      if (!isGuest && !eimzo.selectedCertificate.isRegistered) {
        const challengeRes = await createChallenge.request({});
        if (!challengeRes.success || !challengeRes.response?.challenge) {
          throw new Error(t('signing:interface.challengeFailed'));
        }
        const ch = challengeRes.response;
        const regPkcs7 = await eimzo.signData(ch.challenge, keyId);
        const verifyRes = await verifySignature.request({
          data: { requestId: ch.requestId, pkcs7: regPkcs7 },
        });
        if (!verifyRes.success) {
          throw new Error(t('signing:eimzo.registerFailed'));
        }
        // Refresh registered certs list so the local state is consistent
        getRegisteredCertificates.request();
      }

      // Step 3: Prepare — the signing session for this file, with the bytes
      // to sign (the stamped rendition).
      const prepareResponse = await prepareSignRequest.request({
        data: { fileUuid: fileRef, signatureType: 'attached', meta: '{}' },
      });
      if (!prepareResponse.success) throw stepFailure(prepareResponse, t);

      const { documentBase64, uuid: sessionUuid } = prepareResponse.response || {};
      if (!documentBase64) {
        throw new Error(t('signing:interface.invalidPrepareResponse'));
      }
      if (!sessionUuid) {
        throw new Error(t('signing:interface.sessionMissing'));
      }

      // Step 4: Sign the document with the same loaded key
      // documentBase64 is already base64 encoded from the server
      const pkcs7 = await eimzo.signData(documentBase64, keyId, true);
      if (!pkcs7) {
        throw new Error(t('signing:interface.signatureGenerationFailed'));
      }

      // Step 5: The trusted timestamp, through docs-api
      const timestampRes = await timestampSignRequest.request({ data: { pkcs7 } });
      if (!timestampRes.success) throw stepFailure(timestampRes, t);
      const timestampedPkcs7 = timestampRes.response?.pkcs7;
      if (!timestampedPkcs7) {
        throw new Error(t('signing:interface.invalidTimestampResponse'));
      }

      // Step 6: Complete the session with the timestamped container
      const completeRes = await completeSignRequest.request({
        data: { sessionUuid, pkcs7: timestampedPkcs7 },
      });
      if (!completeRes.success) throw stepFailure(completeRes, t);

      const result = completeRes.response || {};
      let successMessage = t('signing:interface.signed');
      if (result.isTimestamped && result.tsaTimeStr) {
        successMessage += `. ${t('signing:eimzo.timestampedAt', { time: result.tsaTimeStr })}`;
      }
      toast.success(successMessage);

      setSuccessResult({
        signedAt: result.signedAt,
        isTimestamped: result.isTimestamped,
        tsaTimeStr: result.tsaTimeStr,
        verificationStatus: result.verificationStatus,
        signerSerialNumber: result.signerSubjectSerialNumber,
      });

      // Refresh the participants list — signing backfills the PINFL and full
      // name onto user-only participants, so the signers UI must re-read them.
      refreshCollection();

      if (onSigningComplete) onSigningComplete(result);

    } catch (err) {
      console.error('Direct signing error:', err);
      toast.error(err.message || t('signing:interface.signFailed'));
      if (STALE_SLOT.has(err?.code)) refreshCollection();
    } finally {
      setSubmitting(false);
    }
  };

  // The initiator sets who may open the verification page (signing:write).
  const me = isGuest ? null : currentUserUuid();
  const isInitiator = canManage && me != null
    && String(field(signerCollection, 'initiator_user_uuid')) === String(me);
  const verificationBlock = signerCollection?.verificationCode && fileRef ? (
    <VerificationBlock
      collection={{ ...signerCollection, fileName: document?.name }}
      fileUuid={fileRef}
      isInitiator={isInitiator}
      guestToken={guestToken}
      onChanged={refreshCollection}
    />
  ) : null;

  // Don't show anything for ordinary documents
  if (approvalState === 'ordinary') {
    return null;
  }

  // If successfully signed, show success card
  if (successResult) {
    return (
      <Card className="w-full sm:w-80 shrink-0 h-fit bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-green-600 dark:text-green-500">
            <CheckCircle2 className="w-5 h-5" />
            {t('signing:interface.signed')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-green-600 dark:text-green-500 bg-green-500/10 p-3 rounded-lg border border-green-500/20">
            {t('signing:interface.signedDesc')}
          </div>

          <div className="space-y-2 text-sm">
            {successResult.signedAt && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">{t('signing:eimzo.signedAt')}:</span>
                <span className="font-medium">{new Date(successResult.signedAt).toLocaleString()}</span>
              </div>
            )}

            {successResult.isTimestamped && successResult.tsaTimeStr ? (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">{t('signing:eimzo.timestamp')}:</span>
                <span className="font-medium font-mono text-xs">{successResult.tsaTimeStr}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {t('signing:eimzo.noTimestamp')}
              </div>
            )}

            {successResult.verificationStatus && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">{t('signing:status.status')}:</span>
                <Badge variant="outline" className="w-fit text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                  {successResult.verificationStatus}
                </Badge>
              </div>
            )}

            {successResult.signerSerialNumber && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">{t('signing:eimzo.serialNumber')}:</span>
                <span className="font-mono text-xs break-all">{successResult.signerSerialNumber}</span>
              </div>
            )}
          </div>

          {signingActions}
          {verificationBlock}
        </CardContent>
      </Card>
    );
  }

  return (
      <>
        <Card className="w-full sm:w-80 shrink-0 h-fit bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <span className="w-7 h-7 rounded-md bg-brand/10 flex items-center justify-center shrink-0"><PenTool className="w-4 h-4 text-brand" /></span>
              {t('signing:interface.titleSign')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {signingEnded ? null : allCandidatesSigned ? (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-500 bg-green-500/10 p-3 rounded-lg border border-green-500/20">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{t('signing:interface.fullySigned')}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/40 p-3 rounded-md border border-border">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-brand" />
                <span>{t('signing:interface.noRequestNotice')}</span>
              </div>
            )}

            {/* Candidates Loading State */}
            {getSignersCollection.loading && (
              <div className="flex justify-center p-4">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Candidates overview */}
            {!getSignersCollection.loading && signerCollection?.participants && signerCollection.participants.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    {t('signing:candidates.title')}
                  </span>
                  {canManage && !signingEnded && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-brand dark:text-[#6FA4F9] hover:text-[#3570E0] hover:bg-brand/10"
                      onClick={() => setShowCandidatesManager(true)}
                    >
                      {t('signing:candidates.manage')}
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {[...(signerCollection.participants)].sort((a, b) => {
                    const oA = a.participantOrder ?? a.participant_order ?? 0;
                    const oB = b.participantOrder ?? b.participant_order ?? 0;
                    return oA - oB;
                  }).map((c, i) => {
                    const pinfl = participantIdLabel(c);
                    const isSigned = isParticipantSigned(c);
                    const isDeclined = c.status === 'rejected';
                    const name = c.signature?.certificate?.subjectCommonName || c.fullName || c.full_name;
                    return (
                      <div key={c.uuid} className={`flex items-center gap-2 px-2.5 py-2 rounded-md text-xs border transition-all ${isSigned
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                        : isDeclined
                          ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                          : 'bg-muted/30 border-border'
                        }`}>
                        {(signerCollection.procedureType || signerCollection.procedure_type) === 'sequential' && (
                          <span className="w-5 h-5 rounded-full bg-brand/10 flex items-center justify-center shrink-0 text-[10px] font-bold text-brand dark:text-[#6FA4F9]">
                            {(c.participantOrder ?? c.participant_order ?? i) + 1}
                          </span>
                        )}
                        <SignerStatusIcon status={c.status} />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate font-medium text-foreground">
                            {name || (pinfl ? `${t('signing:candidates.pinfl')}: ${pinfl}` : '\u2014')}
                          </span>
                          {name && pinfl && (
                            <span className="text-[10px] text-muted-foreground font-mono">
                              {t('signing:candidates.pinfl')}: {pinfl}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {signingActions}

            {verificationBlock}

            {/* Add Signers button when no candidates yet - hidden for guest */}
            {canManage && (!signerCollection?.participants || signerCollection.participants.length === 0) && fileRef && (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-brand dark:text-[#6FA4F9] border-brand/30 dark:border-brand/40 hover:bg-brand/10"
                onClick={() => setShowCandidatesManager(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t('signing:candidates.addSigners')}
              </Button>
            )}

            <div className="space-y-3 pt-2">
              {/* WARNING: No candidates - show prominently before even connecting */}
              {!getSignersCollection.loading && (!signerCollection?.participants || signerCollection.participants.length === 0) && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-200 dark:border-red-800">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span className="font-medium">{t('signing:candidates.noCandidatesWarning')}</span>
                </div>
              )}

              {placeholders && !allCandidatesSigned && !signingEnded && (
                <div
                  data-testid="placeholders-missing"
                  data-mode={placeholders.mode}
                  className={`text-sm p-3 rounded-md border space-y-1 ${placeholdersBlock
                    ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    {t('signing:placeholders.missingTitle')}
                  </div>
                  <ul className="list-disc pl-6 text-xs">
                    {placeholders.missing.map((m) => <li key={m.key}>{m.label}</li>)}
                  </ul>
                  <p className="text-xs opacity-80">
                    {t(placeholdersBlock ? 'signing:placeholders.blocked' : 'signing:placeholders.warn')}
                  </p>
                </div>
              )}

              {!allCandidatesSigned && !signingEnded && (
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Shield className="w-3.5 h-3.5" />
                {t('signing:eimzo.title')}
              </div>
              )}

              {allCandidatesSigned || signingEnded ? null : !canSign ? (
                <p data-testid="no-sign-scope" className="text-sm text-muted-foreground bg-muted/40 p-3 rounded-md border border-border">
                  {t('signing:interface.noSignScope')}
                </p>
              ) : !eimzo.connected ? (
                <EimzoGate eimzo={eimzo} onRetry={handleConnectEIMZO} t={t} />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-green-500 font-medium bg-green-500/10 p-2 rounded border border-green-500/20">
                    <UnlockIcon className="w-4 h-4" />
                    {t('signing:eimzo.connected')}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Select
                      value={eimzo.selectedCertificate?.serialNumber || ''}
                      onValueChange={handleSelectCertificate}
                    >
                      <SelectTrigger className="w-full h-auto py-2">
                        <SelectValue placeholder={t('signing:eimzo.selectCertificate')}>
                          {eimzo.selectedCertificate && (
                            <div className="flex items-center gap-2 text-left">
                              {eimzo.selectedCertificate.O && !eimzo.selectedCertificate.O.includes('.') ? (
                                <Building className="w-4 h-4 text-blue-600 shrink-0" />
                              ) : (
                                <Users className="w-4 h-4 text-green-600 shrink-0" />
                              )}
                              <div className="truncate">
                                <span className="font-medium text-xs">
                                  {eimzo.selectedCertificate.O && !eimzo.selectedCertificate.O.includes('.')
                                    ? eimzo.selectedCertificate.O
                                    : eimzo.selectedCertificate.fullName}
                                </span>
                              </div>
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {combinedCertificates.map((cert) => {
                          const isCompany = cert.O && !cert.O.includes('.');
                          return (
                            <SelectItem
                              key={cert.serialNumber || cert.id}
                              value={cert.serialNumber || cert.id}
                              disabled={!cert.isOnDevice || cert.isExpired}
                              className={!cert.isOnDevice || cert.isExpired ? 'opacity-50' : ''}
                            >
                              <div className="flex items-start gap-3 py-1">
                                <div className={`mt-0.5 p-1.5 rounded-full ${isCompany ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-green-100 dark:bg-green-900/40'}`}>
                                  {isCompany ? (
                                    <Building className="w-4 h-4 text-blue-600" />
                                  ) : (
                                    <Users className="w-4 h-4 text-green-600" />
                                  )}
                                </div>
                                <div className="flex flex-col min-w-0 pr-6">
                                  <span className="font-medium text-foreground truncate">
                                    {isCompany ? cert.O : cert.fullName}
                                  </span>
                                  {isCompany && (
                                    <span className="text-xs text-muted-foreground truncate">
                                      {cert.fullName}
                                    </span>
                                  )}
                                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <span className="whitespace-nowrap">
                                      {isCompany ? `${t('signing:eimzo.tin')}: ${maskNationalId(cert.TIN)}` : `${t('signing:eimzo.pinfl')}: ${maskNationalId(cert.PINFL)}`}
                                    </span>
                                    {!cert.isOnDevice && <Badge variant="outline" className="h-4 text-[10px] bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400">{t('signing:eimzo.notOnDevice')}</Badge>}
                                    {cert.isRegistered && cert.isOnDevice && <Badge variant="outline" className="h-4 text-[10px] bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400">{t('signing:eimzo.registered')}</Badge>}
                                    {cert.isOnDevice && (
                                      <>
                                        <span>•</span>
                                        <span className={cert.isExpired ? 'text-destructive font-semibold' : ''}>
                                          {cert.isExpired ? t('signing:eimzo.expired') : `${t('signing:eimzo.validUntil')} ${cert.validTo?.toLocaleDateString(i18n.language)}`}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Certificate expiry warning */}
                  {eimzo.selectedCertificate?.isExpired && (
                    <div className="flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 p-2 rounded border border-amber-500/20">
                      <AlertTriangle className="w-4 h-4" />
                      {t('signing:eimzo.certificateExpired')}
                    </div>
                  )}

                  {/* Already signed warning */}
                  {isSelectedCertAlreadySigned && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-md border border-blue-200">
                      <Shield className="w-4 h-4 shrink-0" />
                      <span>{t('signing:eimzo.alreadySignedKey')}</span>
                    </div>
                  )}

                  {/* Not a candidate warning */}
                  {eimzo.selectedCertificate && !isSelectedCertAlreadySigned && !isUserCandidate && signerCollection?.participants?.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>{t('signing:eimzo.notCandidate')}</span>
                    </div>
                  )}

                  {/* Single "Sign" button — handles cert registration inline if needed */}
                  {eimzo.selectedCertificate && !eimzo.selectedCertificate.isExpired && (
                    <div className="space-y-2">
                      <Button
                        className="w-full text-white font-medium"
                        style={{
                          background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                        }}
                        onClick={handleDirectSign}
                        title={
                          deadlinePassed
                            ? t('signing:notice.signingClosed')
                            : placeholdersBlock ? t('signing:placeholders.blocked') : undefined
                        }
                        disabled={
                          submitting ||
                          deadlinePassed ||
                          placeholdersBlock ||
                          isSelectedCertAlreadySigned ||
                          (!isUserCandidate && !!signerCollection?.participants) ||
                          (signerCollection?.participants?.length === 0)
                        }
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <PenTool className="w-4 h-4 mr-2" />
                        )}
                        {t('signing:interface.sign')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidates Manager Dialog — for whoever may send for signing */}
        {canManage && (
          <CandidatesManager
            open={showCandidatesManager}
            onOpenChange={setShowCandidatesManager}
            fileUuid={fileRef}
            signerCollection={signerCollection}
            onRefresh={refreshCollection}
            document={document}
          />
        )}
        <EImzoSetupModal
          open={setupModalOpen}
          onOpenChange={(open) => {
            setSetupModalOpen(open);
            if (!open) {
              getRegisteredCertificates.request();
              if (eimzo.connected) eimzo.loadCertificates();
            }
          }}
        />
      </>
    );
};

export default SigningPanel;
